<script setup lang="ts">
import type { GalleryItem } from '~/composables/types/event'

defineEmits('click:image')
defineProps<{
  principalImage: GalleryItem
  featuredImages: GalleryItem[]
  hasVerticalVideo: boolean
  isOfferPage?: boolean
}>()
</script>

<template>
  <div
    class="flex gap-2 md:gap-4 h-[220px] sm:h-auto lg:h-[420px] grid-container"
  >
    <slot name="main-image">
      <div
        class="gallery-item w-2/3 lg:w-full col-span-6 object-cover rounded-lg overflow-hidden"
        @click="$emit('click:image')"
      >
        <NuxtImg
          :height="'auto'"
          :preload="true"
          :src="String(principalImage.cropped)"
          :alt="principalImage.alt"
          aspect-ratio="auto"
          loading="eager"
          class="w-full h-full object-cover"
          sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
        >
        </NuxtImg>
        <slot name="action-buttons" />
      </div>
    </slot>
    <div
      class="relative grow"
      :class="{
        'md:hidden': isOfferPage,
      }"
    >
      <div
        class="grid lg:grid-cols-2 grid-rows-2 gap-4 col-span-2 lg:min-w-[420px] lg:w-[420px] h-full w-full"
      >
        <div
          v-for="(_, index) in new Array(4)"
          :key="index"
          class="bg-gray-100 h-full w-full rounded-md"
          :class="[
            {
              'hidden lg:block': index > 1,
            },
          ]"
        ></div>
      </div>
      <div
        class="absolute top-0 flex lg:grid lg:grid-cols-2 gap-4 col-span-2 lg:min-w-[420px] lg:w-[420px] h-full"
      >
        <div
          class="lg:grid grid-rows-2 gap-2 md:gap-4"
          :class="{
            hidden: hasVerticalVideo,
            grid: !hasVerticalVideo,
          }"
        >
          <div
            v-for="(image, index) in featuredImages.slice(0, 2)"
            :key="index"
            class="rounded-md lg:h-[202px]"
          >
            <div
              class="gallery-item h-full rounded-lg overflow-hidden"
              @click="$emit('click:image')"
            >
              <NuxtImg
                :height="'auto'"
                :src="String(image.src)"
                :alt="image.alt"
                aspect-ratio="auto"
                loading="lazy"
                class="min-h-full object-cover min-w-full"
                sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
              />
              <slot name="featured-image-extra" v-bind="image"></slot>
            </div>
          </div>
        </div>
        <slot name="vertical-slot">
          <div class="lg:grid grid-rows-2 gap-4 hidden">
            <div
              v-for="(image, index) in featuredImages.slice(2, 4)"
              :key="index + 2"
              class="rounded-md lg:h-[202px]"
            >
              <div
                class="gallery-item h-full rounded-lg overflow-hidden"
                @click="$emit('click:image')"
              >
                <NuxtImg
                  :height="'auto'"
                  :src="String(image.src)"
                  :alt="image.alt"
                  aspect-ratio="auto"
                  loading="lazy"
                  class="h-full w-full object-cover"
                  sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
                />
                <slot name="featured-image-extra" v-bind="image"></slot>
              </div>
            </div>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>
<style scoped lang="postcss">
.grid-container {
  max-width: calc(100vw - 2rem);
}

.gallery-item {
  position: relative;
  cursor: pointer;
  backface-visibility: hidden;
}

.gallery-item img {
  transition: 300ms all ease;
  transform-origin: center;
  transform: scale3d(1, 1, 1);
}

.gallery-item:hover img {
  transform: scale3d(1.05, 1.05, 1);
}
</style>
